import React, { useMemo, useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Button, Chip, MenuItem, Select, IconButton, Tooltip, Stack, Checkbox } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditNoteIcon from '@mui/icons-material/EditNote';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  STATUS_STYLES,
  SOURCE_LABEL,
  WORKSHOP_HIDDEN_FIELDS,
  REPEAT_VERDICT,
  LINE_ITEM_STATUS_ORDER,
  HEADER_MANUAL_STATUS_ORDER,
  isHeaderInconsistent,
  lineStatusOptions,
} from '../constants';
import { flattenOrders, statusSlug } from '../lib/gridRows';
import { buildOrderUrl } from '../lib/platformLinks';

// Order-level statuses not also available per line (used for the single-row combo).
const ORDER_LEVEL_ONLY = HEADER_MANUAL_STATUS_ORDER.filter((s) => !LINE_ITEM_STATUS_ORDER.includes(s));

const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
const fmtMoney = (n) => (typeof n === 'number' ? gbp.format(n) : '');
const fmtDate = (d) =>
  d instanceof Date && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
    : '';

const selectSx = { fontSize: 12, minWidth: 120 };
const itemSx = { fontSize: 12 };

/** Per-line production status (felt: Uncut/Cut/Sewn/…, or embroidery: Pending/Embroidered/…). */
function LineStatusSelect({ value, onChange, options = LINE_ITEM_STATUS_ORDER }) {
  return (
    <Select size="small" variant="standard" disableUnderline value={options.includes(value) ? value : ''} onChange={(e) => onChange(e.target.value)} sx={selectSx}>
      {options.map((s) => (
        <MenuItem key={s} value={s} sx={itemSx}>{s}</MenuItem>
      ))}
    </Select>
  );
}

/** Header status: "Auto (<roll-up>)" or a manual order-level override. */
function HeaderStatusSelect({ manualStatus, productionStatus, onChange }) {
  return (
    <Select size="small" variant="standard" disableUnderline value={manualStatus || '__auto__'} onChange={(e) => onChange(e.target.value === '__auto__' ? null : e.target.value)} sx={selectSx}>
      <MenuItem value="__auto__" sx={itemSx}>Auto · {productionStatus}</MenuItem>
      {HEADER_MANUAL_STATUS_ORDER.map((s) => (
        <MenuItem key={s} value={s} sx={itemSx}>{s}</MenuItem>
      ))}
    </Select>
  );
}

/** Single-line order: one control for the line's production AND order-level states. */
function CombinedStatusSelect({ value, onLine, onHeader, lineOptions = LINE_ITEM_STATUS_ORDER }) {
  return (
    <Select
      size="small"
      variant="standard"
      disableUnderline
      value={value || ''}
      onChange={(e) => {
        const v = e.target.value;
        if (lineOptions.includes(v)) onLine(v);
        else onHeader(v);
      }}
      sx={selectSx}
    >
      {lineOptions.map((s) => (
        <MenuItem key={s} value={s} sx={itemSx}>{s}</MenuItem>
      ))}
      {ORDER_LEVEL_ONLY.map((s) => (
        <MenuItem key={s} value={s} sx={{ ...itemSx, fontStyle: 'italic' }}>{s}</MenuItem>
      ))}
    </Select>
  );
}

function RepeatChip({ repeat }) {
  if (!repeat || repeat.verdict === REPEAT_VERDICT.NO) return null;
  if (repeat.verdict === REPEAT_VERDICT.YES) {
    return <Chip size="small" color="warning" label="Repeat" />;
  }
  // "maybe" — settled state shown if confirmed/denied
  if (repeat.confirmedByUser === true) return <Chip size="small" color="warning" label="Repeat" />;
  if (repeat.confirmedByUser === false) return null;
  return <Chip size="small" variant="outlined" color="warning" label="Maybe?" />;
}

function PostBy({ order }) {
  const eff = order.dates?.postByEffective;
  const platform = order.dates?.postByPlatform;
  const target = order.dates?.postByTarget;
  const platformSooner =
    platform && target && platform.getTime() < target.getTime();
  return (
    <Tooltip
      title={`Target: ${fmtDate(target) || '—'}  •  Platform: ${fmtDate(platform) || '—'}`}
      arrow
    >
      <span style={{ fontWeight: platformSooner ? 700 : 400, color: platformSooner ? '#b10202' : 'inherit' }}>
        {fmtDate(eff)}
      </span>
    </Tooltip>
  );
}

// Wrap cell content with an orange marker + "Was: <original>" tooltip when the
// order has an edited (overridden) value at `path`. Uses already-loaded data.
function markCell(order, path, content, label) {
  const original = order?.overrides?.[path];
  if (original == null || original === '') return content;
  return (
    <Tooltip title={label || `Was: ${original}`} arrow>
      <Box sx={{ borderLeft: '3px solid', borderColor: 'warning.main', pl: 0.5, width: '100%' }}>{content}</Box>
    </Tooltip>
  );
}

/**
 * Orders grid — grouped rows (order header + line items), per-status colour,
 * inline status dropdown, whole-group reordering, column show/hide + Workshop view.
 *
 * Props:
 *  - orders: ordered array (by sortKey) of app orders
 *  - loading
 *  - onStatusChange(orderId, status)
 *  - onMove(orderIndex, direction)  // direction -1 (up) / +1 (down)
 */
export default function OrdersGrid({
  orders = [],
  loading = false,
  workshop = false,
  selectedIds,
  onToggleSelect,
  onHeaderStatus,
  onLineStatus,
  onMove,
  onOpenOrder,
  hasMore = false,
  onLoadMore,
}) {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  // The MIT DataGrid caps pageSize at 100, so we page within the grid (≤100/page)
  // and fetch the next batch when the user reaches the last loaded page.
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });

  useEffect(() => {
    if (workshop) {
      setColumnVisibilityModel(Object.fromEntries(WORKSHOP_HIDDEN_FIELDS.map((f) => [f, false])));
    } else {
      setColumnVisibilityModel({});
    }
  }, [workshop]);

  const rows = useMemo(() => flattenOrders(orders), [orders]);
  const lastIndex = orders.length - 1;

  // Keep the current page in range when the row set shrinks (e.g. switching tabs).
  useEffect(() => {
    setPaginationModel((m) => {
      const maxPage = Math.max(0, Math.ceil(rows.length / m.pageSize) - 1);
      return m.page > maxPage ? { ...m, page: maxPage } : m;
    });
  }, [rows.length]);

  // Paging onto (or past) the last loaded page fetches the next batch, so the
  // grid's own pager drives "Load more" — one continuous list, not two paginations.
  const handlePagination = (model) => {
    setPaginationModel(model);
    const loadedPages = Math.ceil(rows.length / model.pageSize);
    if (hasMore && onLoadMore && model.page >= loadedPages - 1) onLoadMore();
  };

  const columns = useMemo(() => {
    // "single" rows (orders with one line item) carry BOTH order-level and item fields.
    const headerOnly = (render) => (p) =>
      p.row.kind === 'header' || p.row.kind === 'single' ? render(p) : '';
    const itemOnly = (render) => (p) =>
      p.row.kind === 'item' || p.row.kind === 'single' ? render(p) : '';
    return [
      {
        field: 'select',
        headerName: '',
        width: 44,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: headerOnly((p) =>
          onToggleSelect ? (
            <Checkbox
              size="small"
              checked={!!selectedIds?.has(p.row.orderId)}
              onChange={() => onToggleSelect(p.row.orderId)}
            />
          ) : (
            ''
          )
        ),
      },
      {
        field: 'open',
        headerName: '',
        width: 72,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: headerOnly((p) => {
          const url = buildOrderUrl(p.row.order);
          return (
            <Stack direction="row">
              <Tooltip title="Order details / edit" arrow>
                <IconButton size="small" onClick={() => onOpenOrder(p.row.orderId)}>
                  <EditNoteIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              {url && (
                <Tooltip title="Open on the platform" arrow>
                  <IconButton size="small" onClick={() => window.open(url, '_blank', 'noopener')}>
                    <OpenInNewIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          );
        }),
      },
      // Reorder column only when reordering is enabled (Active tab) — avoids an
      // empty column on history/status tabs.
      ...(onMove
        ? [
            {
              field: 'move',
              headerName: '',
              width: 64,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              renderCell: headerOnly((p) => (
                <Stack direction="row">
                  <IconButton size="small" disabled={p.row.orderIndex === 0} onClick={() => onMove(p.row.orderIndex, -1)}>
                    <ArrowUpwardIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton size="small" disabled={p.row.orderIndex === lastIndex} onClick={() => onMove(p.row.orderIndex, 1)}>
                    <ArrowDownwardIcon fontSize="inherit" />
                  </IconButton>
                </Stack>
              )),
            },
          ]
        : []),
      // Order follows the legacy spreadsheet A->AE sequence (with the app controls
      // above, and the Bespoke/Leather + Repeat split kept adjacent at L).
      // A
      { field: 'orderedDate', headerName: 'Date', width: 100, sortable: false, renderCell: headerOnly((p) => fmtDate(p.row.order.dates?.ordered)) },
      // B
      {
        field: 'status',
        headerName: 'Status',
        width: 170,
        sortable: false,
        renderCell: (p) => {
          const { kind, order, lineItem } = p.row;
          if (kind === 'item') {
            return (
              <LineStatusSelect
                value={lineItem.status}
                options={lineStatusOptions(lineItem)}
                onChange={(v) => onLineStatus(order, lineItem.lineId, v)}
              />
            );
          }
          if (kind === 'single') {
            const li = lineItem || order.lineItems?.[0] || {};
            const effective = order.workflow?.manualStatus || li.status;
            return (
              <CombinedStatusSelect
                value={effective}
                lineOptions={lineStatusOptions(li)}
                onLine={(v) => onLineStatus(order, li.lineId, v, { clearManual: true })}
                onHeader={(v) => onHeaderStatus(order, v)}
              />
            );
          }
          const inconsistent = isHeaderInconsistent(order.workflow?.manualStatus, order.lineItems);
          return (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <HeaderStatusSelect
                manualStatus={order.workflow?.manualStatus}
                productionStatus={order.workflow?.productionStatus}
                onChange={(v) => onHeaderStatus(order, v)}
              />
              {inconsistent && (
                <Tooltip title="Manual status doesn't match the line items" arrow>
                  <WarningAmberIcon color="warning" fontSize="inherit" />
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
      // C
      {
        field: 'source',
        headerName: 'Channel',
        width: 90,
        sortable: false,
        renderCell: headerOnly((p) => (
          <Chip size="small" variant="outlined" label={SOURCE_LABEL[p.row.order.source] || p.row.order.source} />
        )),
      },
      // D
      { field: 'platformOrderId', headerName: 'ID', width: 120, sortable: false, renderCell: headerOnly((p) => p.row.order.platformOrderId) },
      // E
      { field: 'platformOrderId2', headerName: 'ID2', width: 130, sortable: false, renderCell: headerOnly((p) => p.row.order.platformOrderId2) },
      // F
      { field: 'buyerUserId', headerName: 'User ID', width: 130, sortable: false, renderCell: headerOnly((p) => p.row.order.buyer?.userId) },
      // G
      { field: 'buyer', headerName: 'Buyer Name', width: 150, sortable: false, renderCell: headerOnly((p) => markCell(p.row.order, 'buyer.name', p.row.order.buyer?.name)) },
      // H
      { field: 'shippingName', headerName: 'Shipping Name', width: 150, sortable: false, renderCell: headerOnly((p) => markCell(p.row.order, 'shipping.name', p.row.order.shipping?.name)) },
      // I
      { field: 'product', headerName: 'Product', width: 240, sortable: false, renderCell: itemOnly((p) => markCell(p.row.order, 'lineItems', p.row.lineItem.product, 'Line items edited')) },
      // J
      { field: 'variation', headerName: 'Variation / Notes', width: 220, sortable: false, renderCell: itemOnly((p) => p.row.lineItem.variation) },
      // K
      { field: 'buyerNotes', headerName: 'Buyer notes', width: 200, sortable: false, renderCell: itemOnly((p) => p.row.lineItem.buyerNotes) },
      // L (LEATHER part of the legacy combined column)
      {
        field: 'bespoke',
        headerName: 'Bespoke / Leather',
        width: 130,
        sortable: false,
        renderCell: headerOnly((p) => (p.row.order.workflow?.isLeather ? <Chip size="small" label="LEATHER" /> : '')),
      },
      // L (past-customer part of the legacy combined column)
      {
        field: 'repeat',
        headerName: 'Repeat',
        width: 90,
        sortable: false,
        renderCell: headerOnly((p) => <RepeatChip repeat={p.row.order.repeatCustomer} />),
      },
      // M
      { field: 'postBy', headerName: 'Post by', width: 110, sortable: false, renderCell: headerOnly((p) => markCell(p.row.order, 'dates.postByTarget', <PostBy order={p.row.order} />)) },
      // N
      { field: 'trackingNumber', headerName: 'Tracking Number', width: 150, sortable: false, renderCell: headerOnly((p) => p.row.order.workflow?.trackingNumber) },
      // O
      { field: 'egNotes', headerName: 'EG notes', width: 200, sortable: false, renderCell: headerOnly((p) => p.row.order.workflow?.egNotes) },
      // P
      { field: 'posted', headerName: 'Posted', width: 76, sortable: false, renderCell: headerOnly((p) => (p.row.order.workflow?.posted ? 'Yes' : '')) },
      // Q
      { field: 'email', headerName: 'Email', width: 200, sortable: false, renderCell: headerOnly((p) => markCell(p.row.order, 'buyer.email', p.row.order.buyer?.email)) },
      // R
      { field: 'phone', headerName: 'Shipping Phone', width: 130, sortable: false, renderCell: headerOnly((p) => markCell(p.row.order, 'shipping.phone', p.row.order.shipping?.phone)) },
      // S
      { field: 'address1', headerName: 'Buyer Address 1', width: 180, sortable: false, renderCell: headerOnly((p) => markCell(p.row.order, 'shipping.address1', p.row.order.shipping?.address1)) },
      // T
      { field: 'address2', headerName: 'Buyer Address 2', width: 150, sortable: false, renderCell: headerOnly((p) => markCell(p.row.order, 'shipping.address2', p.row.order.shipping?.address2)) },
      // U
      { field: 'townCity', headerName: 'Buyer Town/City', width: 130, sortable: false, renderCell: headerOnly((p) => markCell(p.row.order, 'shipping.townCity', p.row.order.shipping?.townCity)) },
      // V
      { field: 'county', headerName: 'Buyer County', width: 120, sortable: false, renderCell: headerOnly((p) => markCell(p.row.order, 'shipping.county', p.row.order.shipping?.county)) },
      // W
      { field: 'postcode', headerName: 'Buyer Postcode', width: 110, sortable: false, renderCell: headerOnly((p) => markCell(p.row.order, 'shipping.postcode', p.row.order.shipping?.postcode)) },
      // X
      { field: 'country', headerName: 'Buyer Country', width: 130, sortable: false, renderCell: headerOnly((p) => markCell(p.row.order, 'shipping.country', p.row.order.shipping?.country)) },
      // (Qty — new; replaces the legacy per-unit row duplication)
      { field: 'qty', headerName: 'Qty', width: 56, sortable: false, align: 'right', headerAlign: 'right', renderCell: itemOnly((p) => p.row.lineItem.quantity) },
      // Y
      { field: 'unitPrice', headerName: 'Price', width: 84, sortable: false, align: 'right', headerAlign: 'right', renderCell: itemOnly((p) => fmtMoney(p.row.lineItem.unitPrice)) },
      // Z
      { field: 'postage', headerName: 'Postage', width: 90, sortable: false, align: 'right', headerAlign: 'right', renderCell: headerOnly((p) => markCell(p.row.order, 'money.postage', fmtMoney(p.row.order.money?.postage))) },
      // AA
      { field: 'refund', headerName: 'Refund', width: 90, sortable: false, align: 'right', headerAlign: 'right', renderCell: headerOnly((p) => markCell(p.row.order, 'money.refund', fmtMoney(p.row.order.money?.refund))) },
      // AB
      { field: 'discount', headerName: 'Discount', width: 90, sortable: false, align: 'right', headerAlign: 'right', renderCell: headerOnly((p) => markCell(p.row.order, 'money.discount', fmtMoney(p.row.order.money?.discount))) },
      // AC
      { field: 'total', headerName: 'Total', width: 90, sortable: false, align: 'right', headerAlign: 'right', renderCell: headerOnly((p) => fmtMoney(p.row.order.money?.total)) },
      // AD
      { field: 'service', headerName: 'Postage2', width: 130, sortable: false, renderCell: headerOnly((p) => markCell(p.row.order, 'shipping.service', p.row.order.shipping?.service)) },
      // AE
      { field: 'ioss', headerName: 'IOSS', width: 64, sortable: false, renderCell: headerOnly((p) => (p.row.order.shipping?.ioss ? 'IOSS' : '')) },
    ];
  }, [onHeaderStatus, onLineStatus, onMove, lastIndex, selectedIds, onToggleSelect, onOpenOrder]);

  // Per-status row colours + header/item styling.
  const statusSx = useMemo(() => {
    const sx = {
      '& .eg-header .MuiDataGrid-cell': { fontWeight: 600 },
      '& .eg-item': { fontStyle: 'normal' },
    };
    Object.entries(STATUS_STYLES).forEach(([status, st]) => {
      sx[`& .row-${statusSlug(status)}`] = {
        backgroundColor: st.bg,
        color: st.fg,
        ...(st.strike ? { textDecoration: 'line-through' } : {}),
      };
      sx[`& .row-${statusSlug(status)}:hover`] = { backgroundColor: st.bg };
    });
    return sx;
  }, []);

  return (
    <Box>
      {Object.keys(columnVisibilityModel).length > 0 && !workshop && (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button size="small" startIcon={<ViewColumnIcon />} onClick={() => setColumnVisibilityModel({})}>
            Show all columns
          </Button>
        </Stack>
      )}
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        getRowClassName={(p) => `row-${statusSlug(p.row.status)} ${p.row.kind === 'header' ? 'eg-header' : 'eg-item'}`}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        disableRowSelectionOnClick
        density="compact"
        hideFooterSelectedRowCount
        getRowHeight={() => 'auto'}
        pageSizeOptions={[50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePagination}
        sx={{
          ...statusSx,
          height: 'calc(100vh - 210px)',
          fontSize: '0.78rem',
          '& .MuiDataGrid-columnHeaderTitle': { fontSize: '0.72rem', fontWeight: 700 },
          '& .MuiDataGrid-cell': {
            borderColor: 'rgba(0,0,0,0.08)',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: 1.3,
            py: 0.5,
            alignItems: 'flex-start',
          },
        }}
      />
    </Box>
  );
}
